import { parseVigenciaDate } from '../lib';

export const toValidDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const isValidDateValue = (value) => Boolean(toValidDate(value));

export const dateOnly = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const getRequestEffectiveDate = (request) => {
  const createdDate = toValidDate(request.fields?.Created ?? request.createdTime);
  const startDate = toValidDate(request.fields?.FECHA_INICIO);

  if (!createdDate) return startDate;
  if (!startDate) return createdDate;

  return new Date(Math.min(createdDate.getTime(), startDate.getTime()));
};

export const calculateDaysBetween = (startDateStr, endDateStr) => {
  try {
    if (!isValidDateValue(startDateStr) || !isValidDateValue(endDateStr)) {
      return null;
    }

    const start = toValidDate(startDateStr);
    const end = toValidDate(endDateStr);

    start.setUTCHours(0, 0, 0, 0);
    end.setUTCHours(0, 0, 0, 0);

    if (end < start) {
      return null;
    }

    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    return Math.max(1, diffDays);
  } catch {
    return null;
  }
};

export const getPeriodBounds = (rec, tipo) => {
  const iniStr =
    tipo === 'dia_economico' ? rec.fields.DE_Inicio_Vigencia : rec.fields.VAC_Inicio_Vigencia;
  const finStr =
    tipo === 'dia_economico' ? rec.fields.DE_Fin_Vigencia : rec.fields.VAC_Fin_Vigencia;
  return {
    iniStr,
    finStr,
    ini: parseVigenciaDate(iniStr, false),
    fin: parseVigenciaDate(finStr, true),
  };
};

export const assignApprovedRequestsToPeriods = (userRecords, approvedRequests) => {
  const sortedRecords = [...userRecords].sort((a, b) => {
    const dateA = new Date(a.fields.VAC_Inicio_Vigencia || '');
    const dateB = new Date(b.fields.VAC_Inicio_Vigencia || '');
    return dateA - dateB;
  });

  const sortedRequests = [...approvedRequests].sort(
    (a, b) => (getRequestEffectiveDate(a)?.getTime() || 0) - (getRequestEffectiveDate(b)?.getTime() || 0)
  );

  const periodRequestsMap = new Map();
  sortedRecords.forEach((record) => {
    periodRequestsMap.set(record.id, []);
  });

  sortedRequests.forEach((request) => {
    if (request.fields.APROBACION !== 'APROBADA') {
      return;
    }

    const reqDate = getRequestEffectiveDate(request);
    if (!reqDate) {
      return;
    }

    const tipoPermiso = (request.fields?.TIPO_DE_PERMISO || 'vacaciones').toLowerCase();

    const candidatosConFechas = sortedRecords.filter((rec) => {
      const { iniStr, finStr, ini, fin } = getPeriodBounds(rec, tipoPermiso);

      if ((iniStr === 'No aplica' || !iniStr) && (finStr === 'No aplica' || !finStr)) {
        return false;
      }

      const reqOnly = dateOnly(reqDate);
      const iniOnly = dateOnly(ini);
      const finOnly = dateOnly(fin);

      return reqOnly >= iniOnly && reqOnly <= finOnly;
    });

    let periodo;

    if (candidatosConFechas.length > 0) {
      candidatosConFechas.sort((a, b) => {
        const ab = getPeriodBounds(a, tipoPermiso);
        const bb = getPeriodBounds(b, tipoPermiso);
        const dIni = ab.ini - bb.ini;
        if (dIni !== 0) return dIni;
        return ab.fin - bb.fin;
      });
      periodo = candidatosConFechas[0];
    } else {
      periodo = sortedRecords.find((rec) => {
        const { iniStr, finStr } = getPeriodBounds(rec, tipoPermiso);
        return (iniStr === 'No aplica' || !iniStr) && (finStr === 'No aplica' || !finStr);
      });

      if (!periodo) {
        return;
      }
    }

    const lista = periodRequestsMap.get(periodo.id) || [];
    lista.push(request);
    periodRequestsMap.set(periodo.id, lista);
  });

  return { sortedRecords, periodRequestsMap };
};

export const computeEmployeeBalances = (
  userRecords,
  approvedRequests,
  currentDate = new Date()
) => {
  const { sortedRecords, periodRequestsMap } = assignApprovedRequestsToPeriods(
    userRecords,
    approvedRequests
  );

  return sortedRecords.map((record) => {
    const availableVacationDays = Number(record.fields.VACACIONES) || 0;
    const availableEconomicDays = Number(record.fields.DIAS_ECONOMICOS) || 0;

    const vacIniStr = record.fields.VAC_Inicio_Vigencia;
    const vacFinStr = record.fields.VAC_Fin_Vigencia;
    const deIniStr = record.fields.DE_Inicio_Vigencia;
    const deFinStr = record.fields.DE_Fin_Vigencia;

    const vacFin = parseVigenciaDate(vacFinStr, true);
    const deFin = parseVigenciaDate(deFinStr, true);

    const vacNoAplica =
      (vacIniStr === 'No aplica' || !vacIniStr) && (vacFinStr === 'No aplica' || !vacFinStr);
    const deNoAplica =
      (deIniStr === 'No aplica' || !deIniStr) && (deFinStr === 'No aplica' || !deFinStr);

    const isVacExpired = !vacNoAplica && currentDate > vacFin;
    const isDeExpired = !deNoAplica && currentDate > deFin;

    let periodVacationDays = 0;
    let periodEconomicDays = 0;
    const invalidRequestsForPeriod = [];

    const periodRequests = periodRequestsMap.get(record.id) || [];

    periodRequests.forEach((request) => {
      const aprobacion = request.fields.APROBACION;
      if (aprobacion !== 'APROBADA') {
        return;
      }

      const reqStartDate = request.fields.FECHA_INICIO;
      const reqEndDate = request.fields.FECHA_FIN;
      const diffDays = calculateDaysBetween(reqStartDate, reqEndDate);
      const tipo = (request.fields.TIPO_DE_PERMISO || '').toLowerCase();

      if (diffDays === null) {
        const reason = !reqStartDate
          ? 'SIN_FECHA_INICIO'
          : !reqEndDate
            ? 'SIN_FECHA_FIN'
            : 'FECHAS_INVALIDAS';

        invalidRequestsForPeriod.push({
          id: request.id,
          reason,
        });
        return;
      }

      if (tipo === 'vacaciones') {
        periodVacationDays += diffDays;
      } else if (tipo === 'dia_economico') {
        periodEconomicDays += diffDays;
      }
    });

    const preRegisteredVacationDaysUsed = Number(record.fields.USOS_Vacaciones) || 0;
    const preRegisteredEconomicDaysUsed = Number(record.fields.USOS_ECONOMICOS) || 0;

    const capVac = Math.max(0, availableVacationDays - preRegisteredVacationDaysUsed);
    const capEco = Math.max(0, availableEconomicDays - preRegisteredEconomicDaysUsed);

    if (isVacExpired) {
      periodVacationDays = availableVacationDays;
    }
    if (isDeExpired) {
      periodEconomicDays = availableEconomicDays;
    }

    const ecoOverflow = Math.max(0, periodEconomicDays - capEco);

    periodVacationDays = Math.min(periodVacationDays, capVac);
    periodEconomicDays = Math.min(periodEconomicDays, capEco);

    const finalVacationDaysUsed = isVacExpired
      ? availableVacationDays
      : Math.min(availableVacationDays, preRegisteredVacationDaysUsed + periodVacationDays);
    const finalEconomicDaysUsed = Math.min(
      availableEconomicDays,
      preRegisteredEconomicDaysUsed + periodEconomicDays
    );

    const vacationDaysRemaining = Math.max(0, availableVacationDays - finalVacationDaysUsed);
    const economicDaysRemaining = Math.max(0, availableEconomicDays - finalEconomicDaysUsed);

    return {
      ...record,
      vacationDaysUsed: finalVacationDaysUsed,
      economicDaysUsed: finalEconomicDaysUsed,
      vacationDaysRemaining,
      economicDaysRemaining,
      _isVacExpired: isVacExpired,
      _isDeExpired: isDeExpired,
      _invalidRequestsForPeriod: invalidRequestsForPeriod,
      _ecoOverflow: ecoOverflow,
    };
  });
};

export const sumRemainingBalances = (processedRecords = []) => ({
  vacationDaysRemaining: processedRecords.reduce(
    (total, record) => total + (record.vacationDaysRemaining || 0),
    0
  ),
  economicDaysRemaining: processedRecords.reduce(
    (total, record) => total + (record.economicDaysRemaining || 0),
    0
  ),
});

export const filterApprovedRequestsForEmployee = (requests, loggedInEmail, employeeId) =>
  requests.filter((record) => {
    const recordEmail = String(record.fields?.EMAIL || '')
      .trim()
      .toLowerCase();
    const normalizedLoggedInEmail = String(loggedInEmail || '')
      .trim()
      .toLowerCase();
    return (
      (recordEmail === normalizedLoggedInEmail ||
        String(record.fields?.ID_Empleado) === String(employeeId)) &&
      record.fields?.APROBACION === 'APROBADA'
    );
  });
